/**
 * A VIDEO BLOCK AS IT STANDS (0144): processing, refused or playing, by the video editor's own panes
 * (../../videos/VideoBlock.tsx) with their controls off - a block is taken out of the article by the
 * block list's own "Remove". The state is read here and never kept in the draft: a conversion
 * finishing is not the author's edit, and must not start an autosave.
 */
import { Text } from '@mantine/core';
import { useCallback } from 'react';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import type { ArticleVideo } from '../../../features/cabinet/api/editor-api';
import { useTranslation } from '../../../i18n';
import { CurrentVideo } from '../../videos/VideoBlock';
import {
  PLAYER_RETRY_GAP_MS,
  VIDEO_POLL_MS,
  useArticleVideo,
} from '../../videos/use-article-video';
import type { ArticleVideoState } from '../../videos/use-article-video';

/** The state of one block's video, kept current by the same reads as a video draft's. */
export function useBlockVideo(
  articleId: string,
  blockId: string,
  initial: ArticleVideo | null,
): ArticleVideoState {
  const api = useEditorApi();
  const read = useCallback(
    (signal: AbortSignal) => api.getArticleVideoBlock(articleId, blockId, signal),
    [api, articleId, blockId],
  );
  return useArticleVideo(articleId, initial, VIDEO_POLL_MS, PLAYER_RETRY_GAP_MS, read);
}

async function nothing(): Promise<void> {
  // The read-only pane offers no removal; the type still asks for one.
}

export function BlockVideoState({ state }: { readonly state: ArticleVideoState }) {
  const { t } = useTranslation('video');
  const { t: ta } = useTranslation('articles');
  if (state.video === null) {
    return (
      <Text size="sm" c="dimmed">
        {ta('editor.blocks.video.missing')}
      </Text>
    );
  }
  return (
    <CurrentVideo
      video={state.video}
      reading={state.reading}
      readFailure={state.readFailure}
      onReadAgain={state.readAgain}
      playbackFailed={state.playbackFailed}
      disabled
      busy={false}
      orientationError={undefined}
      onPlayerFailed={state.refresh}
      onReplace={() => undefined}
      onRemove={nothing}
      t={t}
    />
  );
}

/** A read view's or a preview's video block: the same panes, on their own. */
export function BlockVideoPlayer({
  articleId,
  blockId,
  video,
}: {
  readonly articleId: string;
  readonly blockId: string;
  readonly video: ArticleVideo | null | undefined;
}) {
  return <BlockVideoState state={useBlockVideo(articleId, blockId, video ?? null)} />;
}
